import { Plus, UserPlus, X } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Md } from '@/components/Md';
import ChallengeFilesSection from './ChallengeFilesSection';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const CAT_OPTIONS = ['alimentação', 'hidratação', 'comportamento', 'estilo de vida', 'infantil'];

/**
 * Detail view de um desafio. Render-only; recebe estado e handlers do orquestrador para
 * garantir zero regressões. data-testids preservados.
 */
export default function ChallengeDetailView(props) {
  const {
    selected, participants, goals, stats, files, dailyStats, weeklyMood, inactiveList,
    showAddGoal, setShowAddGoal, goalForm, setGoalForm,
    showAddParticipant, setShowAddParticipant,
    participantSearch, setParticipantSearch, participantSearchResults, participantSearchLoading, clients,
    showFileUpload, setShowFileUpload, fileForm, setFileForm,
    onBack, onToggle, onOpenBroadcast, onOpenDuplicate, onDelete,
    onAddParticipant, onRemoveParticipant, onOpenNotes,
    onAddGoal, onDeleteGoal, GoalsListComp, onChallengeRefresh,
    onRankingTypeChange, onVisibilityChange, onFileUpload, onDeleteFile,
  } = props;

  const partIds = participants.map(p => p.patient_id);

  return (
    <div className="fade-in" data-testid="desafio-detail">
      <button onClick={onBack} className="text-sm text-brand-green mb-3 hover:underline">&larr; Voltar aos desafios</button>
      <div className="flex items-start justify-between mb-5">
        <div>
          <h2 className="font-heading font-medium text-2xl text-brand-text">{selected.title}</h2>
          {selected.description && <Md as="p" className="text-sm text-brand-muted mt-1">{selected.description}</Md>}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="text-xs text-brand-muted">{fmtDatePt(selected.start_date)} → {fmtDatePt(selected.end_date)}</span>
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${selected.active ? 'bg-brand-mint/20 text-brand-green' : 'bg-brand-bg text-brand-muted'}`}>{selected.active ? 'Ativo' : 'Inativo'}</span>
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${selected.visibility === 'public' ? 'bg-blue-50 text-blue-600' : 'bg-brand-bg text-brand-muted'}`} data-testid="detail-visibility-badge">{selected.visibility === 'public' ? 'Público' : 'Silencioso'}</span>
            {selected.target_audience && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-brand-gold/15 text-brand-gold-hover" data-testid="detail-target-audience">{selected.target_audience}</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={onToggle} className="border border-brand-border text-brand-green rounded-full px-3 py-1.5 text-xs font-medium">{selected.active ? 'Desativar' : 'Ativar'}</button>
          <button onClick={onOpenBroadcast} className="border border-brand-border text-brand-green rounded-full px-3 py-1.5 text-xs font-medium hover:bg-brand-bg" data-testid="broadcast-btn">Enviar mensagem</button>
          <button onClick={onOpenDuplicate} className="border border-brand-border text-brand-muted rounded-full px-3 py-1.5 text-xs font-medium hover:bg-brand-bg" data-testid="duplicate-btn">Duplicar</button>
          <button onClick={() => onDelete(selected.id)} className="border border-red-200 text-red-500 rounded-full px-3 py-1.5 text-xs font-medium">Eliminar</button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-white border border-brand-border rounded-2xl p-4 text-center">
            <p className="text-2xl font-heading font-semibold text-brand-text">Dia {stats.day_num}</p>
            <p className="text-[10px] text-brand-muted">de {stats.total_days}</p>
          </div>
          <div className="bg-white border border-brand-border rounded-2xl p-4 text-center">
            <p className="text-2xl font-heading font-semibold text-brand-green">{stats.group_pct}%</p>
            <p className="text-[10px] text-brand-muted">Adesão grupo</p>
          </div>
          <div className="bg-white border border-brand-border rounded-2xl p-4 text-center">
            <p className="text-2xl font-heading font-semibold text-brand-text">{stats.participants}</p>
            <p className="text-[10px] text-brand-muted">Participantes</p>
          </div>
        </div>
      )}

      {/* Leaderboard (perda de peso ou adesão) */}
      {stats?.leaderboard && stats.leaderboard.length > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5" data-testid="leaderboard">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">
            {stats.ranking_type === 'weight_loss' ? `Ranking — Perda de peso desde ${stats.challenge.start_date}` : 'Ranking — Adesão (% metas)'}
          </h3>
          <ol className="space-y-1.5">
            {stats.leaderboard.map(e => (
              <li key={e.patient_id} className="flex items-center justify-between text-xs bg-brand-bg/40 rounded-xl px-3 py-2" data-testid={`leaderboard-row-${e.patient_id}`}>
                <span className="flex items-center gap-2 min-w-0">
                  {e.position ? <span className={`text-[10px] font-semibold w-5 h-5 rounded-full flex items-center justify-center ${e.position === 1 ? 'bg-amber-200 text-amber-800' : e.position === 2 ? 'bg-gray-200 text-gray-700' : e.position === 3 ? 'bg-orange-200 text-orange-800' : 'bg-brand-bg text-brand-muted'}`}>{e.position}</span> : <span className="text-[10px] w-5 h-5 flex items-center justify-center text-brand-placeholder">—</span>}
                  <span className="text-brand-text truncate">{e.patient_name || e.patient_id}</span>
                </span>
                {stats.ranking_type === 'weight_loss' ? (
                  e.lost_kg == null ? (
                    <span className="text-[10px] text-brand-placeholder italic">sem peso inicial registado</span>
                  ) : (
                    <span className={`text-xs font-semibold ${e.lost_kg > 0 ? 'text-brand-green' : e.lost_kg < 0 ? 'text-red-500' : 'text-brand-muted'}`} data-testid={`leaderboard-kg-${e.patient_id}`}>
                      {e.lost_kg > 0 ? '−' : e.lost_kg < 0 ? '+' : ''}{Math.abs(e.lost_kg).toFixed(1)} kg
                    </span>
                  )
                ) : (
                  <span className="text-xs font-semibold text-brand-green" data-testid={`leaderboard-pct-${e.patient_id}`}>{e.pct ?? 0}%</span>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Participants */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5">
        <div className="flex justify-between mb-3">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Participantes</h3>
          <button onClick={() => setShowAddParticipant(true)} className="text-xs text-brand-green flex items-center gap-1"><UserPlus size={12} /> Adicionar</button>
        </div>
        {participants.length === 0 ? <p className="text-xs text-brand-placeholder">Sem participantes.</p> : (
          <div className="space-y-2">
            {participants.map(p => {
              const pStat = stats?.participant_stats?.find(s => s.patient_id === p.patient_id);
              return (
                <div key={p.id} className="flex items-center justify-between p-2 bg-brand-bg/50 rounded-xl" data-testid={`enrolled-user-${p.patient_id}`}>
                  <div>
                    <p className="text-sm text-brand-text">{p.patient_name || p.patient_id}</p>
                    <p className="text-[10px] text-brand-muted">{p.patient_email}</p>
                    <p className="text-[10px] text-brand-placeholder" data-testid={`enrolled-join-${p.patient_id}`}>Inscrito em {(p.joined_at || '').slice(0, 10)}{pStat?.joined_day ? ` · entrou no dia ${pStat.joined_day}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {pStat && <span className="text-xs font-semibold text-brand-gold-hover" data-testid={`participant-points-${p.patient_id}`}>{pStat.points ?? 0} pts</span>}
                    {pStat && <span className={`text-xs font-medium ${pStat.pct >= 70 ? 'text-brand-green' : 'text-brand-muted'}`} data-testid={`participant-pct-${p.patient_id}`}>{pStat.pct}% adesão</span>}
                    <button onClick={() => onOpenNotes(p)} className="text-[10px] bg-white border border-brand-border rounded-full px-2 py-0.5 text-brand-muted hover:border-brand-green hover:text-brand-green" data-testid={`participant-notes-${p.patient_id}`}>Notas</button>
                    <button onClick={() => onRemoveParticipant(p.patient_id)} className="text-brand-placeholder hover:text-red-500" data-testid={`remove-user-${p.patient_id}`}><X size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Visibility selector */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5" data-testid="visibility-selector">
        <Label className="text-[10px] text-brand-muted uppercase tracking-wider">Visibilidade</Label>
        <select
          value={selected.visibility || 'silent'}
          onChange={(e) => onVisibilityChange(e.target.value)}
          className="w-full mt-1 px-3 py-2 border border-brand-border rounded-xl text-sm bg-white"
          data-testid="visibility-select"
        >
          <option value="public">Público — aparece no catálogo para auto-inscrição</option>
          <option value="silent">Silencioso — só gerido aqui (não aparece no catálogo)</option>
        </select>
      </div>

      {/* Ranking type selector */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5" data-testid="ranking-type-selector">
        <Label className="text-[10px] text-brand-muted uppercase tracking-wider">Tipo de ranking</Label>
        <select
          value={selected.ranking_type || 'adherence'}
          onChange={(e) => onRankingTypeChange(e.target.value)}
          className="w-full mt-1 px-3 py-2 border border-brand-border rounded-xl text-sm bg-white"
          data-testid="ranking-type-select"
        >
          <option value="adherence">Adesão (% metas completas)</option>
          <option value="weight_loss">Perda de peso desde início</option>
          <option value="none">Sem ranking</option>
        </select>
        <p className="text-[10px] text-brand-placeholder mt-1.5">Quando &quot;Sem ranking&quot;, a tabela de classificação fica vazia para todas as participantes.</p>
      </div>

      {/* Daily stats chart */}
      {dailyStats.length > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5" data-testid="daily-stats-chart">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Adesão diária do grupo</h3>
          <div className="flex items-end gap-1 h-32 overflow-x-auto pb-1">
            {dailyStats.map(d => (
              <div key={d.date} className="flex flex-col items-center min-w-[28px] flex-1" title={`${d.date}: ${d.users_done}/${d.total} (${d.pct}%)`} data-testid={`day-bar-${d.day_num}`}>
                <div className="text-[9px] text-brand-muted mb-0.5">{d.pct}%</div>
                <div className="w-full rounded-t bg-brand-green/80 transition-all" style={{ height: `${Math.max(2, d.pct)}%` }} />
                <div className="text-[9px] text-brand-placeholder mt-1">D{d.day_num}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ITEM 3 — Humor médio semanal por participante */}
      {weeklyMood && weeklyMood.rows && weeklyMood.rows.length > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5" data-testid="weekly-mood-panel">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-1">Humor médio semanal</h3>
          <p className="text-[10px] text-brand-placeholder mb-3">{weeklyMood.week_start} a {weeklyMood.week_end} · escala 1-5. A vermelho: precisa de mais apoio (&lt; 3).</p>
          <ul className="space-y-1.5">
            {weeklyMood.rows.map(r => {
              const MOOD_EMOJI = ['😔', '😟', '😐', '🙂', '😊'];
              return (
                <li key={r.patient_id} className={`flex items-center justify-between rounded-xl px-3 py-2 ${r.needs_support ? 'bg-red-50 border border-red-200' : 'bg-brand-bg/40'}`} data-testid={`mood-row-${r.patient_id}`}>
                  <span className={`text-sm truncate flex-1 min-w-0 ${r.needs_support ? 'text-red-700 font-medium' : 'text-brand-text'}`}>{r.patient_name}</span>
                  {r.avg_mood != null ? (
                    <span className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-base">{MOOD_EMOJI[Math.max(0, Math.min(4, Math.round(r.avg_mood) - 1))]}</span>
                      <span className={`text-sm font-semibold ${r.needs_support ? 'text-red-600' : 'text-brand-green'}`} data-testid={`mood-avg-${r.patient_id}`}>{r.avg_mood.toFixed(1)}</span>
                      <span className="text-[9px] text-brand-placeholder">({r.checkins} check-ins)</span>
                      {r.needs_support && <span className="text-[9px] bg-red-100 text-red-700 rounded-full px-2 py-0.5">Precisa de apoio</span>}
                    </span>
                  ) : (
                    <span className="text-[10px] text-brand-placeholder flex-shrink-0">Sem registos esta semana</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Inactive participants */}
      {inactiveList.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5" data-testid="inactive-section">
          <h3 className="font-heading font-medium text-xs text-amber-900 uppercase tracking-wider mb-2">Participantes sem atividade há mais de 3 dias</h3>
          <ul className="space-y-1.5">
            {inactiveList.map(p => (
              <li key={p.patient_id} className="flex items-center justify-between bg-white rounded-xl px-3 py-2" data-testid={`inactive-${p.patient_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-brand-text truncate">{p.patient_name}</p>
                  <p className="text-[10px] text-brand-muted">Última actividade: {p.days_inactive == null ? 'nunca' : `há ${p.days_inactive} dias`}</p>
                </div>
                {p.phone && (
                  <a href={`https://wa.me/${p.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="text-[10px] bg-brand-green text-white rounded-full px-3 py-1 ml-2 flex-shrink-0">WhatsApp</a>
                )}
                {!p.phone && p.email && (
                  <a href={`mailto:${p.email}`} className="text-[10px] bg-brand-bg border border-brand-border rounded-full px-3 py-1 ml-2 flex-shrink-0">Email</a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Files section (extracted) */}
      <ChallengeFilesSection files={files} onOpenUpload={() => setShowFileUpload(true)} onDeleteFile={onDeleteFile} />

      {/* Goals */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5">
        <div className="flex justify-between mb-3">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Metas do desafio</h3>
          <button onClick={() => setShowAddGoal(true)} className="text-xs text-brand-green flex items-center gap-1"><Plus size={12} /> Adicionar meta</button>
        </div>
        {goals.length === 0 ? <p className="text-xs text-brand-placeholder">Sem metas. Adiciona metas ao desafio.</p> : (
          <GoalsListComp goals={goals} challengeId={selected.id} onUpdate={onChallengeRefresh} onDelete={onDeleteGoal} />
        )}
      </div>

      {/* Ranking + daily grid (legacy compact view) */}
      {stats && stats.ranking && stats.ranking.length > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Ranking de adesão</h3>
          <div className="space-y-1.5 mb-4">
            {stats.ranking.map((r, i) => (
              <div key={r.patient_id} className="flex items-center gap-2 p-2 bg-brand-bg/30 rounded-xl">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i === 0 ? 'bg-brand-gold/20 text-brand-gold-hover' : 'bg-brand-bg text-brand-muted'}`}>{i + 1}</span>
                <span className="text-xs text-brand-text flex-1">{r.name}</span>
                <span className="text-xs font-medium text-brand-green">{r.overall}%</span>
                <span className="text-[9px] text-brand-muted">{r.streak} dias seguidos</span>
              </div>
            ))}
          </div>
          <p className="text-[9px] text-brand-muted">Média do grupo: {stats.avg_adherence}%</p>
        </div>
      )}

      {/* Daily participation grid */}
      {stats && stats.ranking && stats.ranking.length > 0 && stats.days && stats.days.length > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5 overflow-x-auto">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Participação diária</h3>
          <table className="w-full text-[9px]">
            <thead><tr>
              <th className="text-left text-brand-muted py-1 sticky left-0 bg-white">Participante</th>
              {stats.days.slice(-14).map(d => <th key={d} className="text-center px-0.5 text-brand-muted">{d.slice(8)}</th>)}
              <th className="text-center text-brand-muted px-1">%</th>
            </tr></thead>
            <tbody>
              {stats.ranking.map(r => (
                <tr key={r.patient_id}>
                  <td className="py-1 text-brand-text sticky left-0 bg-white pr-2">{r.name?.split(' ')[0]}</td>
                  {stats.days.slice(-14).map(d => {
                    const c = r.daily?.[d] || 'grey';
                    return <td key={d} className="text-center"><div className={`w-3 h-3 rounded-sm mx-auto ${c === 'green' ? 'bg-green-500' : c === 'green_retro' ? 'bg-green-400 ring-1 ring-amber-400' : c === 'red' ? 'bg-red-400' : 'bg-gray-200'}`} title={c === 'green_retro' ? 'Registo retroativo' : ''} /></td>;
                  })}
                  <td className="text-center font-medium text-brand-green">{r.overall}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add participant dialog */}
      <Dialog open={showAddParticipant} onOpenChange={(o) => { setShowAddParticipant(o); if (!o) setParticipantSearch(''); }}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Adicionar participante</DialogTitle></DialogHeader>
          <Input
            autoFocus
            value={participantSearch}
            onChange={e => setParticipantSearch(e.target.value)}
            placeholder="Pesquisar por nome ou email..."
            className="rounded-xl border-brand-border mb-2"
            data-testid="participant-search-input"
          />
          <div className="max-h-60 overflow-y-auto space-y-1">
            {(() => {
              const q = participantSearch.trim();
              const baseList = q ? participantSearchResults : clients;
              const filtered = baseList.filter(c => !partIds.includes(c.user_id));
              if (participantSearchLoading) {
                return <p className="text-xs text-brand-muted py-4 text-center" data-testid="participant-search-loading">A pesquisar...</p>;
              }
              if (!q && clients.length === 0) {
                return <p className="text-xs text-brand-muted py-4 text-center">A carregar pacientes...</p>;
              }
              if (filtered.length === 0) {
                return <p className="text-xs text-brand-muted py-4 text-center" data-testid="participant-search-empty">{q ? `Sem resultados para "${participantSearch}".` : 'Todos os pacientes já participam.'}</p>;
              }
              return filtered.map(c => (
                <button key={c.user_id} onClick={() => { onAddParticipant(c.user_id); setShowAddParticipant(false); setParticipantSearch(''); }} className="w-full text-left p-3 rounded-xl hover:bg-brand-mint/10 border border-transparent hover:border-brand-mint/30 transition-all" data-testid={`participant-option-${c.user_id}`}>
                  <p className="text-sm font-medium text-brand-text">{c.name}</p>
                  <p className="text-[10px] text-brand-muted">{c.email}</p>
                </button>
              ));
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add goal dialog */}
      <Dialog open={showAddGoal} onOpenChange={setShowAddGoal}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Nova meta do desafio</DialogTitle></DialogHeader>
          <p className="text-xs text-brand-muted mb-3">Esta meta será criada para todos os participantes do desafio.</p>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Título</Label><Input value={goalForm.title} onChange={e => setGoalForm({ ...goalForm, title: e.target.value })} placeholder="Ex: Pequeno-almoço antes das 9h" className="rounded-xl border-brand-border" required /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Categoria</Label>
              <div className="flex flex-wrap gap-1.5">{CAT_OPTIONS.map(c => <button key={c} type="button" onClick={() => setGoalForm({ ...goalForm, category: c })} className={`px-2.5 py-1 rounded-full text-xs ${goalForm.category === c ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{c}</button>)}</div>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Tipo</Label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setGoalForm({ ...goalForm, target_type: 'binary' })} className={`flex-1 py-2 rounded-xl text-xs font-medium ${goalForm.target_type === 'binary' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Sim/Não</button>
                <button type="button" onClick={() => setGoalForm({ ...goalForm, target_type: 'quantitative' })} className={`flex-1 py-2 rounded-xl text-xs font-medium ${goalForm.target_type === 'quantitative' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Quantitativa</button>
              </div>
            </div>
            {goalForm.target_type === 'quantitative' && (
              <div><Label className="text-xs text-brand-muted mb-1 block">Valor alvo</Label><Input type="number" value={goalForm.target_value} onChange={e => setGoalForm({ ...goalForm, target_value: e.target.value })} placeholder="Ex: 8" className="w-24 rounded-xl border-brand-border" /></div>
            )}
            <div className="border-t border-brand-border pt-3">
              <label className="flex items-center gap-2 text-xs text-brand-text cursor-pointer">
                <input type="checkbox" checked={goalForm.recurrent} onChange={e => setGoalForm({ ...goalForm, recurrent: e.target.checked })} className="rounded" data-testid="recurrent-checkbox" />
                <span>Meta recorrente (só activa em certos dias do desafio)</span>
              </label>
              {goalForm.recurrent && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <div>
                    <Label className="text-[10px] text-brand-muted mb-1 block">Dia de início</Label>
                    <Input type="number" min="1" value={goalForm.start_day} onChange={e => setGoalForm({ ...goalForm, start_day: parseInt(e.target.value) || 1 })} className="rounded-xl border-brand-border" data-testid="start-day-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-brand-muted mb-1 block">Frequência</Label>
                    <select value={goalForm.frequency} onChange={e => setGoalForm({ ...goalForm, frequency: e.target.value })} className="w-full px-2 py-2 border border-brand-border rounded-xl text-xs bg-white" data-testid="frequency-select">
                      <option value="daily">Diária</option>
                      <option value="every_2_days">A cada 2 dias</option>
                      <option value="weekly">Semanal</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-brand-muted mb-1 block">Dia fim (opcional)</Label>
                    <Input type="number" min="1" value={goalForm.end_day} onChange={e => setGoalForm({ ...goalForm, end_day: e.target.value })} className="rounded-xl border-brand-border" data-testid="end-day-input" />
                  </div>
                </div>
              )}
            </div>
            <button onClick={onAddGoal} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Criar meta</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* File upload modal */}
      <Dialog open={showFileUpload} onOpenChange={setShowFileUpload}>
        <DialogContent className="rounded-3xl border-brand-border" data-testid="file-upload-dialog">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Adicionar ficheiro</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Ficheiro (PDF, áudio, imagem ou vídeo — máx. 60 MB)</Label>
              <input type="file" accept=".pdf,audio/*,image/*,video/*" onChange={e => setFileForm({ ...fileForm, file: e.target.files?.[0] || null })} className="w-full text-xs" data-testid="file-input" />
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Título</Label>
              <Input value={fileForm.title} onChange={e => setFileForm({ ...fileForm, title: e.target.value })} placeholder="Ex: PDF de boas-vindas" className="rounded-xl border-brand-border" data-testid="file-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Dia do desafio</Label>
                <Input type="number" min="1" value={fileForm.day} onChange={e => setFileForm({ ...fileForm, day: parseInt(e.target.value) || 1 })} className="rounded-xl border-brand-border" data-testid="file-day-input" />
              </div>
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Tipo</Label>
                <select value={fileForm.file_type} onChange={e => setFileForm({ ...fileForm, file_type: e.target.value })} className="w-full px-3 py-2 border border-brand-border rounded-xl text-sm bg-white" data-testid="file-type-select">
                  <option value="pdf">PDF</option>
                  <option value="audio">Áudio</option>
                  <option value="image">Imagem</option>
                  <option value="video">Vídeo</option>
                </select>
              </div>
            </div>
            <button onClick={onFileUpload} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="file-upload-btn">Carregar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
